/**
 * Phone "Today": the next water round, then one card per open pond (active or
 * preparing). Every pond is listed (entry isn't gated by assignment); a
 * technician's assigned ponds come first under "Your ponds".
 *
 * Progress is the server's per-slot rule off the checklist: a slot is done
 * (completed_slots) when its field tests are filled, started (started_slots) when
 * some are; lab tests are optional extras and never count.
 */
import { DateBar } from '@/components/entry/DateBar';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { displayPhDate, formatPhDate, phMinutesOfDay } from '@/lib/phDate';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { useMembership } from '@/hooks/useMembership';
import { usePonds, type PondListItem } from '@/hooks/usePonds';
import { useMobileNavStore } from '@/stores/mobileNavStore';
import { ALL_SLOTS, SLOT_META, roundSlotAt } from '@/lib/waterModel';
import type { ChecklistPond, ReadingSlot } from '@/types/entry';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { usePondsForDateWithPreparing } from '@/components/entry/adaptive/entryData';
import { StartPrepDialog } from '@/components/pond/page/PrestockingTab';

interface Props {
  farmId: string | undefined;
  date: string;
  /** The `date` search param, if any, carried into the links so a backfill day stays put. */
  linkDate?: string;
  /** Choose another day (an unset search param means today); back-filled entries are marked late. */
  onDate?: (date: string) => void;
  userLabel: string | null;
  /** A technician's assigned pond ids; null for other roles (no grouping). */
  assignedIds: string[] | null;
}

const bigButton =
  'flex min-h-12 items-center justify-center rounded-xl border text-[15px] font-semibold touch-manipulation';
const primaryButton = cn(bigButton, 'border-primary-600 bg-primary-600 text-white');
const plainButton = cn(bigButton, 'border-gray-300 bg-white text-gray-900');

interface SlotProgress {
  done: ReadingSlot[];
  started: ReadingSlot[];
}

function slotProgress(pond: ChecklistPond): SlotProgress {
  const done = ALL_SLOTS.filter((s) => pond.completedSlots.includes(s));
  const started = ALL_SLOTS.filter((s) => pond.startedSlots.includes(s) && !pond.completedSlots.includes(s));
  return { done, started };
}

const countText = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

// Mirrors waterModel's SLOT_START_MINUTES, which isn't exported.
const SLOT_DUE_MINUTES: Record<ReadingSlot, number> = {
  '5am': 5 * 60,
  '7am': 7 * 60,
  '3pm': 15 * 60,
  '7pm': 19 * 60,
  '10pm': 22 * 60,
};

function dueSlotsFor(date: string, now: Date): ReadingSlot[] {
  const today = formatPhDate(now);
  if (date !== today) return date < today ? ALL_SLOTS : [];
  const minutes = phMinutesOfDay(now);
  return ALL_SLOTS.filter((s) => minutes >= SLOT_DUE_MINUTES[s]);
}

export function PhoneToday({ farmId, date, linkDate, onDate, userLabel, assignedIds }: Props) {
  const { data: checklist, isLoading, isError, refetch } = usePondsForDateWithPreparing(farmId, date);
  const { currentFarm, farms } = useCurrentFarm();
  const { isSupervisorOrAbove } = useMembership();
  // The checklist only lists ponds with a crop, so a farm with no ponds at all comes from the pond list.
  const farmPonds = usePonds(farmId);
  const farmHasNoPonds = farmPonds.isSuccess && farmPonds.data.length === 0;
  const openMenu = useMobileNavStore((s) => s.setOpen);
  const menuOpen = useMobileNavStore((s) => s.open);

  // The due counts and the round change with the clock; a phone app left open or resumed has no other re-render.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => setNowMs(Date.now());
    const onVisible = () => document.visibilityState === 'visible' && tick();
    const id = setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  const now = new Date(nowMs);

  const ponds = (checklist?.ponds ?? []).filter((p) => p.activeCycle || p.preparingCycle);
  // The checklist only has ponds with a crop; the farm's other ponds are where a Farm Technician starts one.
  const idlePonds = isSupervisorOrAbove ? (farmPonds.data ?? []).filter((p) => !ponds.some((c) => c.id === p.id)) : [];
  const active = ponds.filter((p) => p.activeCycle);
  const progress = new Map(active.map((p) => [p.id, slotProgress(p)]));
  const dueSlots = dueSlotsFor(date, now);
  const futureDay = date > formatPhDate(now);
  const pastDay = date < formatPhDate(now);

  const current = roundSlotAt(phMinutesOfDay(now));
  const later = ALL_SLOTS.slice(ALL_SLOTS.indexOf(current));
  const notDone = (s: ReadingSlot) => active.filter((p) => !progress.get(p.id)!.done.includes(s));
  const round = later.find((s) => notDone(s).length > 0) ?? current;
  const due = notDone(round);
  const roundPond = due[0] ?? active[0];
  const yours = assignedIds ? ponds.filter((p) => assignedIds.includes(p.id)) : [];
  const groups =
    yours.length > 0
      ? [
          { title: 'Your ponds', ponds: yours },
          { title: 'Other ponds', ponds: ponds.filter((p) => !assignedIds!.includes(p.id)) },
        ].filter((g) => g.ponds.length > 0)
      : [{ title: null, ponds }];

  return (
    <div className="space-y-3" data-phone-today>
      <header className="space-y-0.5">
        <div className="mb-1.5 flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-gray-900">Today</h1>
          {currentFarm &&
            (farms.length > 1 ? (
              <button
                type="button"
                onClick={() => openMenu(true)}
                aria-label={`Farm: ${currentFarm.name}. Change farm`}
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
                className="-mx-1 -my-2 flex min-h-11 min-w-0 items-center rounded-lg px-1 text-left touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                data-farm-name
              >
                <span className="flex min-w-0 items-center gap-1 rounded-full bg-gray-100 py-1 pl-3 pr-2 text-[13px] font-semibold text-gray-700">
                  <span className="line-clamp-2 break-words">{currentFarm.name}</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                </span>
              </button>
            ) : (
              <span className="min-w-0 truncate text-[13px] font-semibold text-gray-500" data-farm-name>
                {currentFarm.name}
              </span>
            ))}
        </div>
        <p className="text-[13px] text-gray-500">
          {displayPhDate(date, { weekday: 'short', month: 'short', day: 'numeric' })}
          {userLabel && ` · ${userLabel}`}
        </p>
      </header>

      {onDate && <DateBar date={date} today={formatPhDate(now)} onChange={onDate} />}

      {isError && !checklist ? (
        <div
          role="alert"
          className="space-y-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-center text-[15px] text-red-800"
          data-today-error
        >
          <p className="font-semibold">Couldn't load today's list</p>
          <button type="button" onClick={() => refetch()} className={primaryButton}>
            Try again
          </button>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {roundPond && (
            <Link
              to="/entry"
              search={{ pond: roundPond.id, scope: 'single', slot: round, date: linkDate }}
              className="flex min-h-11 items-center justify-between rounded-2xl bg-primary-900 px-4 py-3.5 text-white"
              data-next-round
            >
              <span className="flex flex-col gap-0.5">
                <span className="text-xs font-semibold uppercase tracking-wider opacity-80">Next round</span>
                <span className="text-[17px] font-bold">
                  {SLOT_META[round].label} readings · {due.length === 0 ? 'all done' : countText(due.length, 'pond')}
                </span>
              </span>
              <ChevronRight className="h-6 w-6" aria-hidden="true" />
            </Link>
          )}

          {ponds.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
              {farmPonds.isPending ? (
                <LoadingSpinner />
              ) : farmHasNoPonds ? (
                <>
                  <p>
                    No ponds yet.
                    {!isSupervisorOrAbove && " Ask your Farm Technician to add this farm's ponds."}
                  </p>
                  {isSupervisorOrAbove && (
                    <Link to="/ponds" className={cn(primaryButton, 'mt-4')}>
                      Add pond
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <p>No ponds to enter for today.</p>
                  <p className="mt-2">
                    {isSupervisorOrAbove
                      ? idlePonds.length > 0
                        ? 'No pond has an active crop or is being prepared. Start one below.'
                        : 'No pond has an active crop or is being prepared. Start one from its pond page.'
                      : 'Ponds show here once a Farm Technician starts them.'}
                  </p>
                </>
              )}
            </div>
          ) : (
            groups.map((g) => (
              <section key={g.title ?? 'all'} aria-label={g.title ?? undefined} className="space-y-3" data-pond-group={g.title ?? 'all'}>
                {g.title && <h2 className="pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{g.title}</h2>}
                {g.ponds.map((pond) =>
                  pond.activeCycle ? (
                    <ActivePondCard
                      key={pond.id}
                      pond={pond}
                      progress={progress.get(pond.id)!}
                      dueSlots={dueSlots}
                      futureDay={futureDay}
                      pastDay={pastDay}
                      round={round}
                      linkDate={linkDate}
                    />
                  ) : (
                    <PreparingPondCard key={pond.id} pond={pond} linkDate={linkDate} pastDay={pastDay} />
                  )
                )}
              </section>
            ))
          )}

          {idlePonds.length > 0 && <IdlePonds ponds={idlePonds} farmId={farmId} />}
        </>
      )}
    </div>
  );
}

/** Supervisor+: ponds with no crop yet, each with Start pre-stocking (water-in). */
function IdlePonds({ ponds, farmId }: { ponds: PondListItem[]; farmId: string | undefined }) {
  const navigate = useNavigate();
  const [starting, setStarting] = useState<PondListItem | null>(null);
  return (
    <section aria-label="No crop yet" className="space-y-3" data-idle-ponds>
      <h2 className="pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">No crop yet</h2>
      {ponds.map((pond) => (
        <div
          key={pond.id}
          className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-3.5"
          data-idle-pond
          data-pond-id={pond.id}
        >
          <span className="min-w-0 truncate text-[17px] font-bold text-gray-900">{pond.name}</span>
          <button type="button" onClick={() => setStarting(pond)} className={cn(primaryButton, 'shrink-0 px-4')}>
            Start pre-stocking
          </button>
        </div>
      ))}
      {starting && farmId && (
        <StartPrepDialog
          open
          phone
          farmId={farmId}
          pond={starting}
          onClose={() => setStarting(null)}
          onStarted={() => navigate({ to: '/entry/pond/$pondId', params: { pondId: starting.id }, search: {} })}
        />
      )}
    </section>
  );
}

function ApplicationLink({ pondId, primary, linkDate }: { pondId: string; primary?: boolean; linkDate?: string }) {
  return (
    <Link
      to="/entry/pond/$pondId/application"
      params={{ pondId }}
      search={{ date: linkDate }}
      className={primary ? primaryButton : plainButton}
    >
      + Application
    </Link>
  );
}

function PondTitle({ pond, linkDate }: { pond: ChecklistPond; linkDate?: string }) {
  return (
    <h3 className="min-w-0 flex-1 text-[17px] font-bold text-gray-900">
      <Link
        to="/entry/pond/$pondId"
        params={{ pondId: pond.id }}
        search={{ date: linkDate }}
        className="-mx-1 -my-2 inline-flex min-h-11 max-w-full items-center gap-0.5 rounded-lg px-1 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 active:underline"
      >
        <span className="truncate underline decoration-gray-300 decoration-1 underline-offset-4">{pond.name}</span>
        <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
      </Link>
    </h3>
  );
}

function ActivePondCard({
  pond,
  progress,
  dueSlots,
  futureDay,
  pastDay,
  round,
  linkDate,
}: {
  pond: ChecklistPond;
  progress: SlotProgress;
  dueSlots: ReadingSlot[];
  futureDay: boolean;
  pastDay: boolean;
  round: ReadingSlot;
  linkDate?: string;
}) {
  const dueCount = dueSlots.length;
  const dueDone = progress.done.filter((s) => dueSlots.includes(s)).length;
  const laterOpen = ALL_SLOTS.filter((s) => !dueSlots.includes(s) && !progress.done.includes(s)).length;
  const started = progress.started.length;
  // The bar is the whole day (five rounds), so "2 of 2 due so far" with rounds still to come
  // does not look finished; the due count stays in the text.
  const barMax = ALL_SLOTS.length;
  const caughtUp = dueCount > 0 && dueDone === dueCount && laterOpen > 0;
  // The checklist's feeding "complete" means any one feeding row, so say "logged", never a tick.
  const feedingText =
    pond.completion.feeding === 'complete'
      ? pastDay ? 'Feeding logged' : 'Feeding logged today'
      : pastDay ? 'No feeding logged' : 'No feeding logged yet';
  const samplingText =
    pond.completion.sampling === 'complete' ? 'Sampling ✓' : pond.completion.sampling === 'pending' ? 'Sampling due' : null;
  const dueText =
    dueCount === ALL_SLOTS.length
      ? `${dueDone} of ${dueCount} done`
      : [
          dueCount === 0 ? '0 due yet' : `${dueDone} of ${dueCount} due so far`,
          laterOpen > 0 && `${laterOpen} later today`,
        ]
          .filter(Boolean)
          .join(' · ');
  const progressText = futureDay
    ? 'Not started'
    : `Readings ${dueText}${started > 0 ? ` · ${started} started` : ''}`;
  return (
    <section
      aria-label={pond.name}
      className="space-y-2.5 rounded-2xl border border-gray-200 bg-white p-3.5"
      data-pond-card="active"
      data-pond-id={pond.id}
    >
      <div className="flex items-center justify-between gap-2">
        <PondTitle pond={pond} linkDate={linkDate} />
        <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
          DOC {pond.activeCycle!.doc}
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-3 text-[13px] text-gray-600">
        <span data-readings-progress>{progressText}</span>
        <span>{countText(pond.applicationsCount, 'application')} {pastDay ? 'that day' : 'today'}</span>
      </div>
      {!futureDay && (
        <p className="text-[13px] text-gray-600" data-today-others>
          {[feedingText, samplingText].filter(Boolean).join(' · ')}
        </p>
      )}
      <div
        className="h-1.5 overflow-hidden rounded-full bg-gray-200"
        role="progressbar"
        aria-label={`${pond.name} readings done`}
        aria-valuemin={0}
        aria-valuemax={barMax}
        aria-valuenow={progress.done.length}
        aria-valuetext={progressText}
      >
        <div className="h-full bg-primary-600" style={{ width: `${Math.round((progress.done.length / barMax) * 100)}%` }} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Link
          to="/entry"
          search={{ pond: pond.id, scope: 'single', slot: round, date: linkDate }}
          className={caughtUp ? plainButton : primaryButton}
        >
          Readings
        </Link>
        <ApplicationLink pondId={pond.id} linkDate={linkDate} />
        <Link to="/entry/pond/$pondId/feeding" params={{ pondId: pond.id }} search={{ date: linkDate }} className={plainButton}>
          Feeding
        </Link>
        <Link to="/entry/pond/$pondId/sampling" params={{ pondId: pond.id }} search={{ date: linkDate }} className={plainButton}>
          Sampling
        </Link>
      </div>
    </section>
  );
}

function PreparingPondCard({ pond, linkDate, pastDay }: { pond: ChecklistPond; linkDate?: string; pastDay?: boolean }) {
  const prep = pond.preparingCycle!;
  return (
    <section
      aria-label={pond.name}
      className="space-y-2.5 rounded-2xl border border-gray-200 bg-white p-3.5"
      data-pond-card="preparing"
      data-pond-id={pond.id}
    >
      <div className="flex items-center justify-between gap-2">
        <PondTitle pond={pond} linkDate={linkDate} />
        <span className="shrink-0 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-800">
          {prep.prepDay !== null ? `Prep day ${prep.prepDay}` : 'Preparing'}
        </span>
      </div>
      <div className="flex flex-wrap justify-between gap-x-3 text-[13px] text-gray-600">
        <span>
          {prep.plannedStockingDate
            ? `Planned stocking ${displayPhDate(prep.plannedStockingDate, { month: 'short', day: 'numeric' })}`
            : 'No stocking date planned'}
        </span>
        <span>{countText(pond.applicationsCount, 'application')} {pastDay ? 'that day' : 'today'}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Link
          to="/entry/pond/$pondId"
          params={{ pondId: pond.id }}
          search={{ date: linkDate, check: '1' }}
          className={primaryButton}
        >
          + Water check
        </Link>
        <ApplicationLink pondId={pond.id} linkDate={linkDate} />
        <Link
          to="/entry/pond/$pondId"
          params={{ pondId: pond.id }}
          search={{ date: linkDate }}
          className={cn(plainButton, 'col-span-2')}
        >
          Open prep
        </Link>
      </div>
    </section>
  );
}
